const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;

let _patrolId = null;
export const setPatrolId = (id) => { _patrolId = id; };
export const getPatrolId = () => _patrolId;

// MARK: getPatroller (core)
export const getPatroller = async (uid, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/core/users/${ uid }`, payload);
        const json = response && await response.json();
        
        if (response.ok) {
            return json;
        } else if (response.status === 401) {
            return { error: 'Your session has expired or is invalid. Please log out and log back in to continue.' };
        } else if (json.error) {
            return { error: json.error };
        } else {
            return { error: 'Failed to fetch user data.' };
        }
    } catch (error) {
        return { error: error.message };
    }
};

// MARK: getPatrollers (core)
export const getPatrollers = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/core/users`, payload);
        const json = response && await response.json();
        
        if (response.ok) {
            if (Array.isArray(json)) {
                json.sort((a, b) => {
                    const lastNameA = (a.lastName ?? '').toLowerCase();
                    const lastNameB = (b.lastName ?? '').toLowerCase();

                    const cmpLast = lastNameA.localeCompare(lastNameB);
                    if (cmpLast !== 0) return cmpLast;

                    // If last names are equal, sort by first name
                    const firstNameA = (a.firstName ?? '').toLowerCase();
                    const firstNameB = (b.firstName ?? '').toLowerCase();

                    const cmpFirst = firstNameA.localeCompare(firstNameB);
                    if (cmpFirst !== 0) return cmpFirst;

                    // sort by display name if last names and first names are equal
                    const displayNameA = (a.displayName ?? '').toLowerCase();
                    const displayNameB = (b.displayName ?? '').toLowerCase();

                    return displayNameA.localeCompare(displayNameB);
                });
            }

            return json;
        } else if (response.status === 401) {
            return { error: 'Your session has expired or is invalid. Please log out and log back in to continue.' };
        } else if (json.error) {
            return { error: json.error };
        } else {
            return { error: 'Failed to fetch users data.' };
        }
    } catch (error) {
        if (error.message === 'Failed to fetch') {
            return { error: 'Network error. Please try again later.' };
        }

        return { error: error.message };
    }
};

// MARK: getCertification
export const getCertification = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/certify/certifications/${ id }`, payload);

        if (response.ok) {
            return await response.json();
        } else {
            return;
        }
    } catch (error) {
        console.error('getCertification:', error);

        return error;
    }
};

// MARK: getCertifications
export const getCertifications = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/certify/certifications`, payload);

        if (response.ok) {
            return await response.json();
        } else {
            return;
        }
    } catch (error) {
        console.error('getCertification:', error);

        return error;
    }
};

// MARK: addCertification
export const addCertification = async (certification, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(certification)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/certify/certifications`, payload);

        if (response.ok) {
            return await response.json();
        } else {
            const json = await response.json();
            return json.error;
        }
    } catch (error) {
        console.error('addCertification:', error);

        return error;
    }
};

// MARK: updateCertification
export const updateCertification = async (certification, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(certification)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/certify/certifications/${ certification.id }`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json();

            return json.error;
        }
    } catch (error) {
        console.error('updateCertification:', error);

        return error;
    }
};

// MARK: removeCertification
export const removeCertification = async (id, token) => {
    try {
        const payload = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/certify/certifications/${ id }`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json();

            return json.error;
        }
    } catch (error) {
        console.error('removeCertification:', error);

        return error;
    }
};

// MARK: certifyPatroller
export const certifyPatroller = async (patroller, certification, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/certify/patrollers/${ patroller.id }/certifications/${ certification.id }`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json();

            return json.error;
        }
    } catch (error) {
        console.error('certifyPatroller:', error);

        return error;
    }
};

// MARK: decertifyPatroller
export const decertifyPatroller = async (patroller, certification, token) => {
    try {
        const payload = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/certify/patrollers/${ patroller.id }/certifications/${ certification.id }`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json();

            return json.error;
        }
    } catch (error) {
        console.error('decertifyPatroller:', error);

        return error;
    }
};

// MARK: callSubmitGitHubIssue
export const callSubmitGitHubIssue = async (email, type, desc, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(
                {
                    title: `${ email } - ${ type }`,
                    body: desc,
                    labels:["core"]
                }
            )
        };

        const response = await fetch(`${ apiEndpoint }/api/submit-github-issue`, payload);

        return response.ok;
    } catch (error) {
        console.error('callSubmitGitHubIssue:', error);

        return { error: error.message };
    }
};